/**
 * Pure helpers for translating react-hook-form validation errors. Deliberately free of
 * React/Syncfusion dependencies, so that they are testable in isolation and the render path does
 * not crash by accident (see localizeFormErrors).
 */

/**
 * True only for plain object literals ({} or Object.create(null)). Class instances,
 * DOM nodes and Syncfusion component instances return false - localizeFormErrors
 * must not recurse into those.
 */
export function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (value === null || typeof value !== 'object') return false;
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
}

/**
 * Creates a copy of the react-hook-form FieldErrors with translated `message` strings.
 *
 * IMPORTANT: it descends into plain object literals and arrays only. The `ref` field of a
 * FieldError points at a DOM node / a Syncfusion component instance; recursing into it
 * can throw (getters) or run into cyclic structures. A throw during rendering would
 * trigger the global ErrorBoundary and replace the whole page content (including the
 * dialog) with the reload screen - the user sees 'the dialog suddenly went blank'.
 */
export function localizeFormErrors(
  errors: any,
  translateMessage: (message: string) => string,
  seen: WeakSet<object> = new WeakSet()
): any {
  if (Array.isArray(errors)) {
    return errors.map(item => localizeFormErrors(item, translateMessage, seen));
  }
  if (!isPlainObject(errors)) {
    return errors;
  }
  if (seen.has(errors)) return errors;
  seen.add(errors);

  const result: any = {};
  for (const [key, value] of Object.entries(errors)) {
    if (key === 'ref') {
      // Take DOM nodes / component refs over unchanged, never recurse into them.
      result[key] = value;
      continue;
    }
    if (key === 'message' && typeof value === 'string') {
      result[key] = translateMessage(value);
      continue;
    }
    result[key] = localizeFormErrors(value, translateMessage, seen);
  }

  return result;
}
